import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Brand } from './common/Brand';
import { Seo } from './Seo';
import { SocialLinks } from './common/SocialLinks';
import '../pages/Marketing.css';

// Stable "latest" download filenames (mirrors Landing.tsx) — always current.
const DL = {
  win: 'https://localareacloud.com/downloads/LACloud_x64-setup.exe',
  mac: 'https://localareacloud.com/downloads/LACloud_mac.zip',
  linux: 'https://localareacloud.com/downloads/LACloud_amd64.AppImage',
};

export type Section = {
  h2?: string;
  paras?: React.ReactNode[];
  bullets?: React.ReactNode[];
  table?: { head: string[]; rows: (string | { v: string; kind?: 'yes' | 'no' })[][] };
};
export type Faq = { q: string; a: string };
export type Related = { label: string; to: string; note?: string };

export type MarketingContent = {
  seo: { title: string; description: string; path: string };
  eyebrow: string;
  h1: React.ReactNode;
  lead: React.ReactNode;
  /** Primary CTA. Defaults to an OS-aware download button. */
  cta?: { label: string; href: string };
  sections: Section[];
  faqs?: Faq[];
  related?: Related[];
  /** Closing CTA band copy. */
  ctaBand?: { h2: string; p: string };
};

function Cell({ c }: { c: string | { v: string; kind?: 'yes' | 'no' } }) {
  if (typeof c === 'string') return <td>{c}</td>;
  return <td className={c.kind}>{c.v}</td>;
}

export function MarketingPage({ content }: { content: MarketingContent }) {
  // Primary download auto-targets the visitor's OS (deterministic default =
  // Windows so the prerendered HTML and first client render match).
  const [dl, setDl] = useState({ href: DL.win, label: 'Download for Windows' });
  useEffect(() => {
    if (content.cta) return; // page pinned its own CTA
    const ua = navigator.userAgent;
    if (/Macintosh|Mac OS X/i.test(ua)) setDl({ href: DL.mac, label: 'Download for macOS' });
    else if (/Linux/i.test(ua) && !/Android/i.test(ua)) setDl({ href: DL.linux, label: 'Download for Linux' });
  }, [content.cta]);

  const primary = content.cta || dl;

  const faqLd = content.faqs && content.faqs.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: content.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : undefined;

  return (
    <div className="mkt">
      <Seo
        title={content.seo.title}
        description={content.seo.description}
        path={content.seo.path}
        jsonLd={faqLd}
      />

      <header className="nav">
        <div className="wrap nav-in">
          <Link className="brand" to="/"><Brand variant="lockup" height={26} /></Link>
          <span className="nav-sp" />
          <a className="plain" href="https://localareacloud.com/#how">How it works</a>
          <Link className="plain" to="/login">Sign in</Link>
          <a className="btn btn-primary" href={primary.href}>{primary.label}</a>
        </div>
      </header>

      <section className="hero">
        <div className="wrap">
          <p className="eyebrow">{content.eyebrow}</p>
          <h1>{content.h1}</h1>
          <p className="lead">{content.lead}</p>
          <div className="hero-cta">
            <a className="btn btn-primary" href={primary.href}>↓&nbsp; {primary.label}</a>
            <Link className="btn btn-ghost" to="/">See how it works</Link>
          </div>
          <div className="trust">
            <span><i className="dot" /> Windows · macOS · Linux · Android · iPhone</span>
            <span><i className="dot" /> Free for 2 devices</span>
            <span><i className="dot" /> No files ever touch our servers</span>
          </div>
        </div>
      </section>

      <div className="body">
        <div className="wrap">
          {content.sections.map((s, i) => (
            <section className="blk" key={i}>
              {s.h2 && <h2>{s.h2}</h2>}
              {s.paras?.map((p, j) => <p key={j}>{p}</p>)}
              {s.bullets && (
                <ul className="ticks">
                  {s.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              )}
              {s.table && (
                <div className="tblwrap">
                  <table>
                    <thead><tr>{s.table.head.map((h, j) => <th key={j}>{h}</th>)}</tr></thead>
                    <tbody>
                      {s.table.rows.map((row, j) => (
                        <tr key={j}>{row.map((c, k) => <Cell key={k} c={c} />)}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}
        </div>
      </div>

      {content.faqs && content.faqs.length > 0 && (
        <div className="wrap">
          <div className="faq">
            <h2>Frequently asked questions</h2>
            {content.faqs.map((f, i) => (
              <details key={i}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      )}

      {content.related && content.related.length > 0 && (
        <div className="wrap">
          <div className="related">
            <h2>Related</h2>
            <div className="related-grid">
              {content.related.map((r, i) => (
                <Link key={i} to={r.to}>
                  {r.label}
                  {r.note && <span>{r.note}</span>}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <section className="cta-band">
        <div className="wrap">
          <h2>{content.ctaBand?.h2 || 'Your files. Your devices. Nobody else’s cloud.'}</h2>
          <p>{content.ctaBand?.p || 'Free for up to two devices. No account required to try, nothing uploaded to anyone’s servers.'}</p>
          <a className="btn btn-primary" href={primary.href}>↓&nbsp; {primary.label}</a>
          <p className="cta-alt">Also on macOS, Linux, iPhone &amp; Android — <Link to="/">see all platforms</Link>.</p>
        </div>
      </section>

      <footer>
        <div className="wrap foot-in">
          <span className="muted">© 2026 Local Area Cloud · Chrono Travel LLC</span>
          <span className="links">
            <Link to="/">Home</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </span>
          <SocialLinks />
        </div>
      </footer>
    </div>
  );
}
